import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { getWishlist, removeFromWishlist, addToWishlist } from "../api/wishlist";
import { addToCart, getBids, placeBid, deleteBid } from '../api/commerce';
import { recordView, recordClick, recordFavorite } from '../api/commerce';
import { getCredit } from '../api/credit';
import { Info, ImageOff } from "lucide-react";
import { useParams } from "react-router-dom";
import CountdownTimer from './Countdown';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import jalaliday from "jalaliday";
import "dayjs/locale/fa";
import "../css/ProductCard.css";

function ProductCard({ productId, ownerId, images, videos , name, description, artist, style, category, material, feature_one, feature_two,
  length, width, height, weight, date, date_pa, price, isAuthenticated, is_biddable, b_deadline, b_deadline_pa, bid_amount, bidder, user, delivered_by, pickup_address,
  currentUser, bidzone_stored, ownerUsername}) {
    
  const safeImages = Array.isArray(images) ? images : images ? [images] : [];
  const safeVideos = Array.isArray(videos) ? videos : videos ? [videos] : [];
  
  const media = [
    ...safeImages.map(img => ({
      id: img.id,
      type: "image",
      src: img.image,
    })),
    ...safeVideos.map(vid => ({
      id: vid.id,
      type: "video",
      src: vid.video,
    })),
  ];

  const sortedMedia = media.sort((a, b) => {
    if (a.type !== b.type) {
      return a.type === "image" ? -1 : 1;
    }
    return (a.id ?? 0) - (b.id ?? 0); 
  });

  // Transation
  dayjs.extend(utc);
  dayjs.extend(jalaliday);
  const toPersiDigits = (str) =>
    str.replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);

  // Analytics
  const ref = useRef(null);

  const handleClick = async (id) => {
    await recordClick(id);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (entry.isIntersecting) {
          await recordView(productId);
          observer.disconnect();
        }
      },
      {
        threshold: 0.5
      }
    );
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => observer.disconnect();
  }, [productId]);
  

  // Navigation
  const [setlang, setLang] = useState("fa")
  const { lang } = useParams()
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentIndexModal, setCurrentIndexModal] = useState(0);
  const [modalPurch, setModalPurch] = useState(null);
  const parts = [productId, name, artist, description, category, material].filter(Boolean);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentImageIndexModal, setCurrentImageIndexModal] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < sortedMedia.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const goToPreviousModal = () => {
    if (currentIndexModal > 0) {
      setCurrentIndexModal(currentIndexModal - 1);
    }
  };

  const goToNextModal = () => {
    if (currentIndexModal < sortedMedia.length - 1) {
      setCurrentIndexModal(currentIndexModal + 1);
    }
  };

  const openModal = (id) => {
    setIsModalOpen(true);
    handleClick(id);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const currentItem = sortedMedia[currentIndex];
  const currentItemModal = sortedMedia[currentIndexModal];
  const mediaToDisplay = currentItem?.src || null;
  const mediaToDisplayModal = currentItemModal?.src || null;

  // Handle Bidding
  const [bids, setBids] = useState([]);
  const [bidAmount, setBidAmount] = useState("");
  const [isBidModalOpen, setIsBidModalOpen] = useState(false);
  const [infoCredit, setCredit] = useState();
  const [error, setError] = useState("");
  const highestBid = bids.length > 0 ? bids[0].bid_amount : price;
  const location = useLocation();
  const navigate = useNavigate();
  
  // Credit information
  const fetchCredit = async () => {
    try {
      const response = await getCredit();
      const creditData = Array.isArray(response) ? response[-0] : response.data[-0];
      setCredit(creditData);   
    } catch (error) {
      //console.error("Error loading cart:", error);
      setError("Error loading cart:", error);
    } 
  };
  
  useEffect(() => {
    if (isAuthenticated) { 
    fetchCredit();
    }
  }, [isAuthenticated]); 

  const normalizeNumber = (input) => {
    if (input == null || input === "") return "";
    const persianDigits = "۰۱۲۳۴۵۶۷۸۹";
    const arabicDigits = "٠١٢٣٤٥٦٧٨٩";
    let str = String(input).trim();
    // Convert Persian/Arabic digits to English
    str = str.replace(/[۰-۹]/g, (d) => persianDigits.indexOf(d));
    str = str.replace(/[٠-٩]/g, (d) => arabicDigits.indexOf(d));
    // Remove spaces and Persian thousands separator
    str = str.replace(/[\s٬]/g, "");
    // Convert Arabic decimal separator to dot
    str = str.replace(/٫/g, ".");
    const lastComma = str.lastIndexOf(",");
    const lastDot = str.lastIndexOf(".");
    if (lastComma > lastDot) {
      // European format: 1.234,56
      str = str.replace(/\./g, "");
      str = str.replace(",", ".");
    } else {
      // US format: 1,234.56
      str = str.replace(/,/g, "");
    }
    const num = Number(str);
    return Number.isNaN(num) ? null : num.toString();
  };

  const handleBid = async (productId, bidAmount, setBids) => {
    if (!isAuthenticated) {
      navigate(`/${lang}/login`, {
        state: { from: { pathname: `/${lang}/gallery`} },
        replace: true,
      });
      return;
    }
    try {
      const numericAmount = parseFloat(normalizeNumber(bidAmount));

      if (numericAmount < price) {
        window.alert(setlang==="en"?"Your bid cannot be lower than the asking price":"پیشنهاد قیمت باید بالاتر از قیمت درخواستی فروشنده باشه")

      } else if (bids.length > 0 && (numericAmount-highestBid)/highestBid < 0.03) { 
          window.alert(setlang==="en"?"Your bid should be at least 3% higher than the highest bid":"پیشنهاد قیمت جدید باید ۳٪ بالاتر از آخرین پیشنهاد باشه")

      } else if (numericAmount > infoCredit.amount_available_IRT) {

         if (!window.confirm (setlang==="en"?"Balance insufficient, visit your account?":"موجودی حسابت کافی نیست، نمایش موجودی حساب؟")) return ;
          window.open(`/${lang}/profile`, '_blank');

      } else {
        if (!window.confirm (setlang==="en"?"Would you like to place a bid":"میخوای پیشنهاد قیمت رو ثبت کنی؟")) return ;
        
        try {
          await placeBid(productId, numericAmount);
          const updatedBids = await getBids(productId);
          setBids(updatedBids);

        } catch (err) {
          //console.error("Bid failed:", err);
          setError("Bid failed:", err);
        }
      }
    } catch (err) {
      //console.error("Bid failed:", err);
      setError("Bid failed:", err);
    }
  };

  const handleBidDelete = async (productId, bidId, setBids) => {
    if (!window.confirm(setlang==="en"?"Are you sure you want to remove your bid?":"میخوای پیشنهاد خریدت رو حذف کنی؟")) return;
    try {
      await deleteBid(bidId); 
      const updatedBids = await getBids(productId);
      setBids(updatedBids);
    } catch (error) {
      //console.error("Error deleting bid:", error);
      setError("Error deleting bid:", error);
      alert(setlang==="en"?"Failed to delete your bid, please try again.":"مشکلی رخ داد، لطفا دوباره تلاش کن");
    }
  };

  useEffect(() => {
    if (is_biddable) {
      getBids(productId)
        .then(setBids)
        .catch(err => setError(err));
    }
  }, [productId, is_biddable]);

  
  // Add to Cart
  const handleAddToCart = async (productId) => {
    if (!isAuthenticated) {
      navigate(`/${lang}/login`, {
        state: { from: { pathname: `/${lang}/gallery` } },
        replace: true,
      });
      return;
    }
    try {
      await addToCart(productId, 1);
      alert(setlang==="en"?"Added to cart!":"این محصول به سبد خریدت اضافه شد");
    } catch (error) {
      //console.error("Add to Cart Error:", error);
      setError("Add to Cart Error:", error)
      alert(setlang==="en"?"Something went wrong. Please try again.":"مشکلی رخ داد، لطفا دوباره وارد شو");
    }
  };
  
  const handleContact = () => {
    if (!isAuthenticated) {
        navigate(`/${lang}/login`, {
        state: { from: { pathname: `/${setlang}/Contactform/${ownerUsername}/${productId}` } },
        replace: true,
      });
    }
  };

  // Add to Wishlist
  const [wishlist, setWishlist] = useState([]);
  
  useEffect(() => {
    if (!isAuthenticated) {
      setWishlist([]); 
      return;
    }

    getWishlist()
      .then(res => {
        if (Array.isArray(res.data)) {
          setWishlist(res.data);
        } else if (res.data.results) {
          setWishlist(res.data.results);
        }
      })
      .catch(err => {
        //console.error("Error fetching wishlist:", err.response?.data || err.message);
        setError("Error fetching wishlist:", err.response?.data || err.message);
        setWishlist([]);
      });
  }, [isAuthenticated]);

  const handleToggleWishlist = (productId) => {
    if (!user) {
      navigate(`/${lang}/login`, { state: { from: location }, replace: true });
      return;
    }

    // Check if product is already in wishlist
    const existingItem = wishlist.find((item) => item.product === productId);
    
    if (existingItem) {
      removeFromWishlist(existingItem.id)
        .then(() => {
          setWishlist(wishlist.filter((item) => item.id !== existingItem.id));
        })
        .catch((err) =>
          // console.error("Remove failed:", err.response?.data || err.message)
          setError("Remove failed:", err.response?.data || err.message)
        );
    } else {
      recordFavorite(productId)
      addToWishlist(productId)
        .then((res) => {
          setWishlist([...wishlist, res.data]);

        })
        .catch((err) =>
          setError("Add failed:", err.response?.data || err.message)
        );
    }
  };


  useEffect (() => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if(lang !== "fa" && lang !== "en") {
      if ( userTimezone === "Asia/Tehran") {
        setLang(`fa`);
      } else {
        setLang(`en`);
      }
    } else {
      setLang(lang)
    }
  }, [lang])


  return (
    <>
      {/* Product Card */}
      <div className="product-card" ref={ref}>
        <div className="image-container">
        {currentItem ? (
          currentItem.type === "image" ? (
            <img src={mediaToDisplay} 
            alt={name}
            className="product-image"
            onClick={()=>openModal(productId)}
            />
          ) : (
            <video 
            src={mediaToDisplay}  
            controls 
            className="product-image"
            onClick={()=>openModal(productId)}
            />
          )
        ) : (
          <ImageOff size={160} className="logo-image" onClick={()=>openModal(productId)}/>
        )}
          
          {/* Image Navigation Arrows */}
          <button
            onClick={goToPrevious}
            className="nav-arrow left"
            hidden={currentIndex === 0}
          >
            &#8592;
          </button>
          <button
            onClick={goToNext}
            className="nav-arrow right"
            hidden={currentIndex >= sortedMedia.length - 1}
          >
            &#8594;
          </button>
        </div>
          <h2 className="product-title">{name}</h2>

        <div className="product-footer-info">
          {artist? (
            setlang === "en" ?
            <div className="product-author"> By: {artist.slice(0,50)}</div>:
            <>
            <div className="product-author"> {artist.slice(0,50)}</div><span>:اثر</span> 
            </>
            ) : category ? (
              <span className="product-author"> {category.slice(0,50)}</span>
            ) : style ? (
              <span className="product-author"> {style.slice(0,50)}</span>
            ) : material ? (
              <span className="product-author"> {material.slice(0,50)}</span>
            ) : feature_one ? ( 
              <span className="product-author"> {feature_one.slice(0,50)}</span>
            ) : description ? ( 
              <span className="product-author"> {description.slice(0,50)}</span>
            ): " "
          }
        </div> 
        {setlang==="en"?
        <div className="product-price" >IRT {new Number(price).toLocaleString()}</div>
        :
        <div className="product-price" >IRT {toPersiDigits(new Number(price).toLocaleString())}</div>
        }

        {/* Details Button to Open Modal */}
        <div className="product-footer">
          <button onClick={()=>openModal(productId)} className="details-button">
            {setlang === "en" ? "View Details" : "نمایش آگهی"}
          </button>
          <Link to={`/${setlang}/productitenary/${parts.join("/")}`} onClick={()=>handleClick(productId)}> 
            <button className="details-button" ><Info size={15} /></button>
          </Link>
        </div>
      </div>

      {/* Modal Section */}
      {isModalOpen && (
        <div onClick={handleOverlayClick} className="modal-overlay">
          <div className="modal-card-prod" onClick={e => e.stopPropagation()}>
            {/* Close Button */}
            <button onClick={closeModal} className="close-button">
              &#10005;
            </button>

            {/* Image + Info Layout */}
            <div className="modal-body-prod">
              {/* Left: Image */}
              <div className="modal-image-section">
                <div className="modal-image-cont">
                  {currentItemModal ? (
                    currentItemModal.type === "image" ? (
                      <img src={mediaToDisplayModal} 
                      alt={name}
                      className="modal-image-large"
                      onClick={()=>openModal(productId)}
                      />
                    ) : (
                      <video 
                      src={mediaToDisplayModal}  
                      controls 
                      className="modal-image-large"
                      onClick={()=>openModal(productId)}
                      />
                    )
                  ) : (
                    <ImageOff size={160} className="logo-image" onClick={()=>openModal(productId)}/>
                  )}
                </div>

                {/* Navigation Arrows */}
                <button
                  onClick={goToPreviousModal}
                  className="nav-arrow left"
                  hidden={currentIndexModal === 0}
                >
                  &#8592;
                </button>
                <button
                  onClick={goToNextModal}
                  className="nav-arrow right"
                  hidden={currentIndexModal >= sortedMedia.length - 1}
                >
                  &#8594;
                </button>
              </div>

              {/* Right: Product Info */}
                <div className="modal-info-section">
                  <h3 className="modal-title">{name}</h3>

                {description && (
                  setlang === "en" ? (
                    <div className="info-row">
                      <div><span className="info-label">Description: </span></div>
                      <p className="modal-description"> {description}</p>
                    </div>
                  ):(
                    <div className= "info-row-fa">
                      <div><p className="modal-description"> {description}</p></div>
                      <div><span className="info-label"> :توضیحات</span></div>
                    </div>
                  )
                )}

                {artist && (
                  setlang === "en" ? (
                  <div className="info-row">
                    <span className="info-label">By: </span>
                    <span className="modal-author"> {artist}</span>
                  </div>
                  ):(
                  <div className="info-row-fa">
                    <span className="modal-author"> {artist}</span>
                    <span className="info-label"> :اثر</span>
                  </div>
                  )
                )}
                
                {category && (
                  setlang === "en" ? (
                  <div className="info-row">
                    <span className="info-label">Category: </span>
                    <div><span className="modal-author">{category}</span></div>
                  </div>
                  ):(
                  <div className="info-row-fa">
                    <div><span className="modal-author">{category}</span></div>
                    <span className="info-label"> :دسته‌بندی</span>
                  </div>
                  )
                )}

                {style && (
                  setlang === "en" ? (
                  <div className="info-row">
                    <span className="info-label">Style: </span>
                    <div><span className="modal-author"> {style}</span></div>
                  </div>
                  ):(
                  <div className="info-row-fa">
                    <div><span className="modal-author"> {style}</span></div>
                    <span className="info-label"> :استایل</span>
                  </div>
                  )
                )}

                {material && (
                  setlang === "en" ? (
                    <div className= "info-row" >
                      <span className="info-label">Material: </span>
                      <div><span className="modal-author">{material}</span></div>
                    </div>
                  ):(
                    <div className= "info-row-fa">
                      <div><span className="modal-author">{material}</span></div>
                      <span className="info-label"> :جنس ساخت</span>
                    </div>
                  )
                )}

                {setlang === "en" ? (
                    <div className="info-row">
                      <span className="info-label">{length ? "Dimensions: ": width ? "Dimensions: ": height ? "Dimensions: ":null}</span>
                      <div><span className="modal-author">{ length ? "L " + length + " cm x " : " " }{ width ? "W " + width + " cm x " : " " }{ height ? "H " + height + " cm" : " " }</span></div>
                    </div>
                  ):(
                    <div className="info-row-fa">
                      <div><span className="modal-author">{ length ? toPersiDigits(new Number(length).toLocaleString()) + " طول x " : null }{ width ? toPersiDigits(new Number(width).toLocaleString()) + " عرض x " : null }{ height ?  toPersiDigits(new Number(height).toLocaleString()) + " ارتفاع" : null }</span></div>
                      <span className="info-label">{length ? " :(سانتیمتر)ابعاد": width ? " :(سانتیمتر)ابعاد": height ? " :(سانتیمتر)ابعاد":null}</span>
                    </div>
                  )
                }
                {weight && (
                  setlang === "en" ? (
                    <div className= "info-row" >
                      <span className="info-label">Weight(kg): </span>
                      <div><span className="modal-author">{(weight)}</span></div>
                    </div>
                  ):(
                    <div className= "info-row-fa">
                      <div><span className="modal-author">{toPersiDigits((weight))}</span></div>
                      <span className="info-label"> :(kg)وزن</span>
                    </div>
                  )
                )}
                
                {feature_one && (
                  setlang === "en" ? (
                  <div className="info-row">
                    <span className="info-label">Features: </span>
                    <div><span className="modal-author">{feature_one}</span></div>
                  </div>
                  ):(
                  <div className="info-row-fa">
                    <div><span className="modal-author">{feature_one}</span></div>
                    <span className="info-label"> :ویژگی‌ها</span>
                  </div>
                  )
                )}

                {feature_two && (
                  setlang === "en" ? (
                    <div className="info-row">
                      <div className="modal-author"> {feature_two}</div>
                    </div>
                    ):(
                    <div className="info-row-fa">
                      <div className="modal-author"> {feature_two}</div>
                    </div>
                    )
                  )}

                {delivered_by && (
                  setlang === "en" ? (
                  <div className="info-row">
                    <span className="info-label">Delivery Mode: </span>
                    <div><span className="modal-author">{delivered_by}</span></div>
                  </div>
                  ):(
                  <div className="info-row-fa">
                    <div><span className="modal-author">{delivered_by}</span></div>
                    <span className="info-label"> :روش ارسال محصول</span>
                  </div>
                  )
                )}
                
                {pickup_address && (
                  setlang === "en" ? (
                  <div className="info-row">
                    <div><span className="modal-author"> {pickup_address}</span></div>
                  </div>
                  ):(
                  <div className="info-row-fa">
                    <div><span className="modal-author"> {pickup_address}</span></div>
                  </div>
                  )
                )}

                {setlang === "en" ? (
                  <div className="info-row">
                    <span className="info-label">Placed on: </span>
                    <span className="modal-date">{date_pa}</span>
                  </div>
                ):(
                  <div className="info-row-fa">
                    <span className="modal-date">{toPersiDigits(date_pa)}</span>
                    <span className="info-label"> :تاریخ آگهی</span>
                  </div>
                )}
                  
                {setlang === "en" ? 
                  <div className="info-row">
                    <span className="info-label">Asking Price: </span>
                    <span className="modal-price">IRT {new Number(price).toLocaleString()}</span>
                  </div> :
                  <div className="info-row-fa">
                    <span className="modal-price-fa">تومان</span>
                    <span className="modal-price-fa">{toPersiDigits(new Number(price).toLocaleString())}</span>
                    <span className="info-label"> :قیمت درخواستی</span>
                  </div>
                }

                {/* Actions */}
                {currentUser?.id !== ownerId ? ( 
                  <>
                    <div className="modal-actions">
                      {is_biddable ? (
                        <>
                          {isAuthenticated ? (
                            <Link style={{textDecoration:"none"}} to={`/${setlang}/contactform/${ownerUsername}/${productId}`}>
                              <button className="secondary-button">{setlang === "en" ? "Contact Seller" : "تماس با فروشنده" }</button>
                            </Link>
                          ) : (
                            <button
                              onClick={handleContact}
                              className="secondary-button">{setlang === "en" ? "Contact Seller" : "تماس با فروشنده" }</button>
                          )}

                          {setlang === "en" ? 
                          <button className="secondary-button" onClick={() => handleToggleWishlist(productId)}>
                            {wishlist.some((item) => item.product === productId)
                              ? "Remove from Wishlist"
                              : "Add to Wishlist"}
                          </button> :
                          <button className="secondary-button" onClick={() => handleToggleWishlist(productId)}>
                            {wishlist.some((item) => item.product === productId)
                              ? "حذف از علاقه‌مندی‌ها"
                              : "اضافه به علاقه‌مندی‌ها"}
                          </button>
                          }

                            <p>IRT <input
                              type="decimal"
                              className="bid-section"
                              value={bidAmount}
                              onChange={e => setBidAmount(e.target.value)}
                              placeholder={setlang === "en" ? "Enter your bid" : "قیمت پیشنهادی" }
                              /></p>
                            <button
                              onClick={() => handleBid(productId, bidAmount, setBids)}
                              className="bid-button"
                              >{setlang === "en" ? "Place Bid" : "پیشنهاد خرید" }</button>

                          <CountdownTimer targetTime={b_deadline} targetTime_pa={b_deadline_pa} setlang={setlang}/>
                          <div className= {setlang === "en" ? "bid-footnote" : "bid-footnote-fa" }>
                            {setlang === "en" ? 
                            <p>
                              <strong>Note:</strong> You may place multiple bids before the auction deadline. 
                              Only the <span className="highlight">highest valid bid</span> at the time of the deadline 
                              will be considered for selection. Once the deadline has passed, no further bids will be accepted.
                            </p> : 
                            <p>
                              <strong>توجه: </strong> 
                                شما می‌توانید برای آیتم‌های مزایده‌ای تا پایان زمان شرکت در مزایده بیش از یک بار پیشنهاد قیمت بدهید و یا پیشنهادهای
                                  خود را حذف کنید. <span className="highlight"> بالاترین پیشنهاد </span> در لحظه پایان مزایده به عنوان برنده مزایده و خریدار کالا مشخص خواهد شد. در صورت برنده شدن
                                  در مزایده مبلغ رزرو شده از حساب شما کسر خواهد شد و  شما می‌توانید فرآیند دریافت کالا را در صفحه سفارش‌های خود دنبال کنید
                            </p>
                            }
                          </div>
                          
                          {/* Bid History */}
                          <div className={setlang === "en" ? "bid-history" : "bid-history-fa" }>
                            <h4>{setlang === "en" ?  "Bid History" : "مزایده در حال انجام" }</h4>
                            {Array.isArray(bids) && bids.length > 0 ? (
                              <table className="bid-table">
                                <thead>
                                  <tr>
                                    <th>{setlang === "en" ? "Bidder" : "کاربر" }</th>
                                    <th>{setlang === "en" ? "Amount" : "پیشنهاد" }</th>
                                    <th>{setlang === "en" ? "Date" : "تاریخ" }</th>
                                    <th>{setlang === "en" ? "Status" : "وضعیت" }</th>
                                    <th>{setlang === "en" ? "Cancel" : "لغو" }</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {bids.map((b, index) => (
                                    <tr key={b.id}>
                                      <td>{b.bidder_name}</td>
                                      {setlang==="en"? (
                                          <>
                                          <td>IRT {new Number(b.bid_amount).toLocaleString()}</td>
                                          <td>{dayjs.utc(b.created_at).local().calendar("jalali").locale("fa").format("YYYY/MM/DD HH:mm")}</td>
                                          </>
                                        ):(
                                          <>
                                          <td style={{direction:"rtl"}}>{toPersiDigits(new Number(b.bid_amount).toLocaleString())} تومان</td>
                                          <td>{toPersiDigits(dayjs.utc(b.created_at).local().calendar("jalali").locale("fa").format("YYYY/MM/DD HH:mm"))}</td>
                                          </>
                                        )
                                      }   
                                      <td>
                                        {index === 0 ? (
                                          <span className="highest-bid">{setlang === "en" ?  "Highest" : "بالا" }</span>
                                        ) : (
                                          <span className="outbid">{setlang === "en" ?  "Outbid" : "پایین" }</span>
                                        )}
                                      </td>
                                      {currentUser?.id === b.bidder && ( 
                                        <td>
                                          <button onClick={() => handleBidDelete(productId, b.id, setBids)}>
                                            X
                                          </button>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <p>{setlang === "en" ? "Place the first bid" : "اولین شرکت کننده در مزایده باشید" }</p>
                            )}
                          </div>     
                        </>
                      ) : (
                        <>
                          {isAuthenticated ? (
                            <Link style={{textDecoration:"none"}} to={`/${setlang}/contactform/${ownerUsername}/${productId}`} >
                              <button className="secondary-button">{setlang === "en" ? "Contact Seller" : "تماس با فروشنده" }</button>
                            </Link>
                          ) : (
                            <button
                              onClick={handleContact}
                              className="secondary-button">{setlang === "en" ? "Contact Seller" : "تماس با فروشنده" }</button>
                          )}
                          
                          {setlang === "en" ? 
                            <button className="secondary-button" onClick={() => handleToggleWishlist(productId)}>
                              {wishlist.some((item) => item.product === productId)
                                ? "Remove from Wishlist"
                                : "Add to Wishlist"}
                            </button> :
                            <button className="secondary-button" onClick={() => handleToggleWishlist(productId)}>
                              {wishlist.some((item) => item.product === productId)
                                ? "حذف از علاقه‌مندی‌ها"
                                : "اضافه به علاقه‌مندی‌ها"}
                            </button>
                          }

                          {bidzone_stored? 
                            <button className="secondary-button" onClick={() => handleAddToCart(productId)}>{setlang === "en" ? "Add to Cart" : "اضافه به سبد خرید"}</button>
                            :
                            <button className="secondary-button" onClick={() => setModalPurch(true)}>{setlang === "en" ? "Add to Cart" : "اضافه به سبد خرید"}</button>
                          }
                          
                          {bidzone_stored? 
                            <Link style={{textDecoration:"none"}} to={`/${setlang}/cart`}>
                              <button onClick={() => handleAddToCart(productId)} className="secondary-button"> 
                                {setlang === "en" ? "Buy Now" : "خرید مستقیم" } 
                              </button>
                            </Link>
                            :
                            <button onClick={() => setModalPurch(true)} className="secondary-button"> 
                              {setlang === "en" ? "Buy Now" : "خرید مستقیم" } 
                            </button>
                          }

                          {modalPurch && (
                            <div onClick={handleOverlayClick} className="modal-overlay" > 
                              <div className={setlang==="en"?"purch-modal-container-en":"purch-modal-container-fa"} onClick={e => e.stopPropagation()}>
                                <h1>{setlang==="en"?"Purchase":"روش خرید"}</h1>
                                <p>{setlang==="en"?"This article is not stored by Bidzone, please contact the seller for purchasing":"این محصول توسط BidZone نگهداری نمی‌شود. برای خرید این محصول مستقیم با فروشنده تماس بگیرید"}</p>
                                
                                <div style={{display:"flex", placeContent:"center"}}>
                                  <button onClick={() => setModalPurch(false)} className="c-modal-button">{setlang==="en"?"Close":"بستن"}</button>
                                
                                  {isAuthenticated ? (
                                    <Link style={{textDecoration:"none"}} to={`/${setlang}/contactform/${ownerUsername}/${productId}`} >
                                      <button className="c-modal-button">{setlang === "en" ? "Contact Seller" : "تماس با فروشنده" }</button>
                                    </Link>
                                  ) : (
                                    <button
                                      onClick={handleContact}
                                      className="c-modal-button">{setlang === "en" ? "Contact Seller" : "تماس با فروشنده" }</button>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                        </>
                      )}
                    </div> 
                  </>
                ) : (
                  <>
                  <div className="modal-actions">
                  
                  {is_biddable ? (
                  <>
                    <CountdownTimer targetTime={b_deadline} targetTime_pa={b_deadline_pa}  setlang={setlang}/>
                    <div className= {setlang === "en" ? "bid-footnote" : "bid-footnote-fa" }>
                      {setlang === "en" ? 
                        <p>
                          <strong>Note:</strong> You may place multiple bids before the auction deadline. 
                          Only the <span className="highlight">highest valid bid</span> at the time of the deadline 
                          will be considered for selection. Once the deadline has passed, no further bids will be accepted.
                        </p> : 
                        <p>
                          <strong>توجه: </strong> 
                            شما می‌توانید برای آیتم‌های مزایده‌ای تا پایان زمان شرکت در مزایده بیش از یک بار پیشنهاد قیمت بدهید و یا پیشنهادهای
                              خود را حذف کنید.<span className="highlight"> بالاترین پیشنهاد </span> در لحظه پایان مزایده به عنوان برنده مزایده و خریدار کالا مشخص خواهد شد. در صورت برنده شدن
                              در مزایده مبلغ رزرو شده از حساب شما کسر خواهد شد و  شما می‌توانید فرآیند دریافت کالا را در صفحه سفارش‌های خود دنبال کنید
                        </p>
                      }
                    </div>
                    <div className={setlang === "en" ? "bid-history" : "bid-history-fa" }>
                      <h4>{setlang === "en" ?  "Bid History" : "مزایده در حال انجام" }</h4>
                      {Array.isArray(bids) && bids.length > 0 ? (
                        <table className="bid-table">
                          <thead>
                            <tr>
                              <th>{setlang === "en" ? "Bidder" : "کاربر" }</th>
                              <th>{setlang === "en" ? "Amount" : "پیشنهاد" }</th>
                              <th>{setlang === "en" ? "Date" : "تاریخ" }</th>
                              <th>{setlang === "en" ? "Status" : "وضعیت" }</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bids.map((b, index) => (
                              <tr key={b.id}>
                                <td>{b.bidder_name}</td>
                                {setlang==="en"? (
                                  <>
                                  <td>IRT {new Number(b.bid_amount).toLocaleString()}</td>
                                  <td>{dayjs.utc(b.created_at).local().calendar("jalali").locale("fa").format("YYYY/MM/DD HH:mm")}</td>
                                  </>
                                ):(
                                  <>
                                  <td>{toPersiDigits(new Number(b.bid_amount).toLocaleString())} تومان</td>
                                  <td>{toPersiDigits(dayjs.utc(b.created_at).local().calendar("jalali").locale("fa").format("YYYY/MM/DD HH:mm"))}</td>
                                  </>
                                )
                                }
                                <td>
                                  {index === 0 ? (
                                    <span className="highest-bid">{setlang === "en" ?  "Highest" : "بالا" }</span>
                                  ) : (
                                    <span className="outbid">{setlang === "en" ?  "Outbid" : "پایین" }</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <p>{setlang === "en" ? "No bids until this moment" : "پیشنهادی تا این لحظه وجود ندارد" }</p>
                      )}
                    </div> 
                  </>
                  ):(
                    <>
                      {setlang === "en" ? 
                        <p><small>You placed this item for direct sale on {date}</small></p>:
                        <p style={{textAlign:"right"}}><small>شما این آگهی را در تاریخ {date} ارسال کرده‌اید </small></p>
                      }
                    </>
                  )}
                  </div> 
                  </>
                )}          
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ProductCard;

